import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Crosshair,
  Layers,
  Network,
  PlayCircle,
  Rocket,
  Server,
  ShieldAlert,
  Target,
  ExternalLink,
} from 'lucide-react';
import type {
  Difficulty,
  ScenarioDetail,
  ScenarioDetectionRef,
  ScenarioVm,
} from '../../api/types';

/**
 * ScenarioDetailPanel — the purpose-built scenario detail. Rendered both in
 * the master-detail catalog (right pane) and on the full-page detail route, so
 * the two stay consistent. Pure presentation: the caller owns the deploy
 * mutation/modal and passes an `onDeploy` callback + the full ScenarioDetail.
 *
 * Style: dark + neon-green HUD (Tailwind design tokens). The topology preview
 * reuses CommandCenterView's connected-SVG-graph technique (segment columns,
 * bezier `<path>` edges, animated `flow-edge` class, zone bands) on the
 * scenario's ScenarioVm blueprint shape.
 */

const DIFFICULTY_STYLE: Record<Difficulty, string> = {
  beginner: 'text-neon bg-neon-glow border-neon/40',
  intermediate: 'text-hud-cyan bg-hud-cyan/10 border-hud-cyan/40',
  advanced: 'text-hud-amber bg-hud-amber/10 border-hud-amber/40',
  expert: 'text-hud-red bg-hud-red/10 border-hud-red/40',
};

type DetectionSeverity = ScenarioDetectionRef['severity'];

const SEVERITY_STYLE: Record<DetectionSeverity, string> = {
  informational: 'bg-hud-cyan/10 text-hud-cyan border-hud-cyan/40',
  low: 'bg-neon/10 text-neon border-neon/40',
  medium: 'bg-hud-amber/10 text-hud-amber border-hud-amber/40',
  high: 'bg-hud-rose/10 text-hud-rose border-hud-rose/40',
  critical: 'bg-hud-red/10 text-hud-red border-hud-red/40',
};

/* ── Topology preview ─────────────────────────────────────────────────────── */

type SegmentKey = 'attacker' | 'external' | 'dmz' | 'internal';

interface SegmentDef {
  key: SegmentKey;
  label: string;
  hex: string;
}

const SEGMENTS: ReadonlyArray<SegmentDef> = [
  { key: 'attacker', label: 'ATTACKER', hex: '#e879f9' },
  { key: 'external', label: 'EXTERNAL', hex: '#22d3ee' },
  { key: 'dmz', label: 'DMZ', hex: '#fbbf24' },
  { key: 'internal', label: 'INTERNAL', hex: '#22c55e' },
];

const COL_X: Record<SegmentKey, number> = {
  attacker: 95,
  external: 350,
  dmz: 605,
  internal: 845,
};

type EdgeKind = 'attack' | 'dnat' | 'trust' | 'logs';

const EDGE_STYLE: Record<EdgeKind, { stroke: string; animated: boolean; width: number }> = {
  attack: { stroke: '#f87171', animated: true, width: 1.7 },
  dnat: { stroke: '#fbbf24', animated: true, width: 1.7 },
  trust: { stroke: 'rgba(34,197,94,0.5)', animated: false, width: 1.2 },
  logs: { stroke: 'rgba(34,211,238,0.5)', animated: true, width: 1.1 },
};

/** Infer the topology column for a blueprint VM from its segment / role. */
function segmentFor(vm: ScenarioVm): SegmentKey {
  const seg = (vm.networkSegment ?? '').toLowerCase();
  const role = (vm.role ?? '').toLowerCase();
  if (seg.includes('attacker') || role.includes('caldera') || role.includes('attacker')) {
    return 'attacker';
  }
  if (
    seg.includes('external') ||
    seg.includes('perimeter') ||
    role.includes('router') ||
    role.includes('firewall')
  ) {
    return 'external';
  }
  if (seg.includes('dmz') || role.includes('web') || role.includes('cms')) return 'dmz';
  return 'internal';
}

/** Stand-in perimeter for blueprints that declare no router VM. */
const IMPLICIT_PERIMETER = {
  hostname: 'perimeter',
  role: 'edge firewall',
  networkSegment: 'external',
} as unknown as ScenarioVm;

/** Whether a VM looks like a SIEM / detection sink (telemetry edge target). */
function isSiem(vm: ScenarioVm): boolean {
  const r = `${vm.role ?? ''} ${vm.hostname ?? ''}`.toLowerCase();
  return r.includes('elastic') || r.includes('siem') || r.includes('wazuh') || r.includes('kibana');
}

export function TopologyPreview({ vms }: { vms: ReadonlyArray<ScenarioVm> }) {
  const grouped = useMemo(() => {
    const map: Record<SegmentKey, ScenarioVm[]> = {
      attacker: [],
      external: [],
      dmz: [],
      internal: [],
    };
    for (const vm of vms) map[segmentFor(vm)].push(vm);
    // Most scenarios declare no router VM. The diagram must still show the
    // attacker reaching the organisation only THROUGH a perimeter -- never
    // wired straight into the internal segments -- so draw an implicit edge
    // node (dashed) in the External column when the blueprint has none.
    if (map.external.length === 0 && map.attacker.length > 0) {
      map.external.push(IMPLICIT_PERIMETER);
    }
    return map;
  }, [vms]);

  const topo = useMemo(() => {
    const VBW = 940;
    const VBH = 260;
    const NW = 120;
    const NH = 46;
    const pos = new Map<string, { x: number; y: number }>();
    for (const seg of SEGMENTS) {
      const list = grouped[seg.key];
      const k = list.length;
      const spacing = Math.min(64, (VBH - 56) / Math.max(k, 1));
      list.forEach((vm, i) => {
        const y = VBH / 2 + (i - (k - 1) / 2) * spacing;
        pos.set(vm.hostname, { x: COL_X[seg.key], y });
      });
    }

    const edges: Array<{ from: string; to: string; kind: EdgeKind }> = [];
    const { attacker: atk, external: ext, dmz, internal: intn } = grouped;
    // Attacker -> perimeter only. The perimeter publishes DMZ services (DNAT)
    // and fronts the internal LAN. There is deliberately NO attacker->internal
    // edge: internal hosts are reached only via the perimeter / a DMZ foothold.
    atk.forEach((a) =>
      ext.forEach((f) => edges.push({ from: a.hostname, to: f.hostname, kind: 'attack' })),
    );
    ext.forEach((f) =>
      dmz.forEach((d) => edges.push({ from: f.hostname, to: d.hostname, kind: 'dnat' })),
    );
    // Internal LAN hangs off the DMZ when there is one (the realistic pivot
    // path), otherwise directly behind the perimeter.
    const lanFrom = dmz.length > 0 ? dmz : ext;
    lanFrom.slice(0, 1).forEach((f) =>
      intn.forEach((n) => edges.push({ from: f.hostname, to: n.hostname, kind: 'trust' })),
    );
    // Telemetry edge: DMZ → SIEM (prefer an explicit SIEM, else first internal).
    const siem = intn.find(isSiem) ?? intn[0];
    if (siem) {
      dmz.forEach((d) => edges.push({ from: d.hostname, to: siem.hostname, kind: 'logs' }));
    }

    return { VBW, VBH, NW, NH, pos, edges };
  }, [grouped]);

  const segmentCount = SEGMENTS.filter((s) => grouped[s.key].length > 0).length;

  if (vms.length === 0) {
    return (
      <div className="card-hud flex flex-col items-center justify-center gap-2 rounded-hud-lg border border-bd bg-deep p-8 text-center">
        <Network className="size-6 text-txt-muted" />
        <div className="text-[12px] text-txt-bright">No topology</div>
        <div className="max-w-[320px] text-[10px] text-txt-muted">
          This scenario defines no VMs in its blueprint.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-hud-lg border border-bd bg-deep">
      <div className="flex items-center justify-between border-b border-bd px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <Network className="size-4 text-neon" />
          <span className="font-display text-[12px] font-semibold text-txt-bright">
            Network topology
          </span>
        </div>
        <span className="text-[9.5px] text-neon">
          {vms.length} nodes · {topo.edges.length} links · {segmentCount} segments
        </span>
      </div>

      <div className="bg-grid overflow-x-auto px-3.5 py-3">
        <svg
          viewBox={`0 0 ${topo.VBW} ${topo.VBH}`}
          className="w-full"
          style={{ minWidth: 660, height: 260 }}
        >
          {/* segment zone bands + labels */}
          {SEGMENTS.map((seg) => {
            const cx = COL_X[seg.key];
            const has = grouped[seg.key].length > 0;
            return (
              <g key={seg.key} opacity={has ? 1 : 0.35}>
                <rect
                  x={cx - 96}
                  y={6}
                  width={192}
                  height={topo.VBH - 12}
                  rx={12}
                  fill="rgba(255,255,255,0.015)"
                  stroke="rgba(255,255,255,0.06)"
                />
                <text
                  x={cx}
                  y={22}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="JetBrains Mono"
                  fontWeight="bold"
                  fill={seg.hex}
                  letterSpacing="1.5"
                >
                  {seg.label}
                </text>
              </g>
            );
          })}

          {/* edges (under nodes) */}
          {topo.edges.map((e, i) => {
            const s = topo.pos.get(e.from);
            const t = topo.pos.get(e.to);
            if (!s || !t) return null;
            const sx = s.x + topo.NW / 2;
            const tx = t.x - topo.NW / 2;
            const mx = (sx + tx) / 2;
            const st = EDGE_STYLE[e.kind];
            return (
              <path
                key={`${e.from}->${e.to}-${i}`}
                d={`M ${sx} ${s.y} C ${mx} ${s.y}, ${mx} ${t.y}, ${tx} ${t.y}`}
                fill="none"
                stroke={st.stroke}
                strokeWidth={st.width}
                strokeDasharray="6 6"
                className={st.animated ? 'flow-edge' : undefined}
              />
            );
          })}

          {/* nodes */}
          {SEGMENTS.flatMap((sg) => grouped[sg.key]).map((vm) => {
            const p = topo.pos.get(vm.hostname);
            if (!p) return null;
            const implicit = vm === IMPLICIT_PERIMETER;
            const seg = segmentFor(vm);
            const hex = SEGMENTS.find((s) => s.key === seg)?.hex ?? '#22c55e';
            const x = p.x - topo.NW / 2;
            const y = p.y - topo.NH / 2;
            return (
              <g key={vm.hostname}>
                <rect
                  x={x}
                  y={y}
                  width={topo.NW}
                  height={topo.NH}
                  rx={7}
                  fill="rgba(8,8,11,0.9)"
                  stroke={hex}
                  strokeWidth={1}
                  strokeDasharray={implicit ? '4 3' : undefined}
                />
                <circle cx={x + 13} cy={y + 15} r={3} fill={hex} />
                <text
                  x={x + 23}
                  y={y + 18}
                  fontSize={11}
                  fontFamily="JetBrains Mono"
                  fontWeight="bold"
                  fill="#fff"
                >
                  {vm.hostname.slice(0, 14)}
                </text>
                <text
                  x={x + 11}
                  y={y + 33}
                  fontSize={7.5}
                  fontFamily="JetBrains Mono"
                  fill={hex}
                  opacity={0.85}
                >
                  {(vm.role || '').toUpperCase().slice(0, 22)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* legend */}
        <div className="mt-1 flex flex-wrap items-center gap-3 text-[9px] text-txt-muted">
          <span className="flex items-center gap-1">
            <span className="inline-block w-4" style={{ borderTop: '2px dashed #f87171' }} /> attack
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-4" style={{ borderTop: '2px dashed #fbbf24' }} /> DNAT
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-4"
              style={{ borderTop: '2px dashed rgba(34,197,94,0.7)' }}
            />{' '}
            LAN
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-4"
              style={{ borderTop: '2px dashed rgba(34,211,238,0.7)' }}
            />{' '}
            telemetry
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Small parts ──────────────────────────────────────────────────────────── */

function StatTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="card-hud flex flex-col gap-1.5 p-3">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-txt-muted">
        <span className="text-neon">{icon}</span>
        {label}
      </div>
      <div className="font-mono text-xl font-bold text-white">{value}</div>
    </div>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

export interface ScenarioDetailPanelProps {
  scenario: ScenarioDetail;
  /** Invoked by the primary "Deploy range" button (caller owns the modal). */
  onDeploy: () => void;
  /** Hide the "Open full page" link (e.g. when already on the full page). */
  hideOpenFullPage?: boolean;
}

export function ScenarioDetailPanel({
  scenario,
  onDeploy,
  hideOpenFullPage,
}: ScenarioDetailPanelProps) {
  const severityByRule = useMemo(() => {
    const m = new Map<string, DetectionSeverity>();
    for (const det of scenario.detections ?? []) m.set(det.sigmaRuleId, det.severity);
    return m;
  }, [scenario.detections]);

  const phaseCount = scenario.phases.length;

  return (
    <div className="font-body p-5">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-lg font-bold leading-tight text-white">
              {scenario.name}
            </h1>
            <span
              className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wider ${DIFFICULTY_STYLE[scenario.difficulty]}`}
            >
              {scenario.difficulty}
            </span>
            <span className="shrink-0 rounded-full border border-bd bg-deep/60 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-txt-2">
              v{scenario.version}
            </span>
          </div>
          <p className="mt-1.5 max-w-2xl text-xs leading-snug text-txt-2">
            {scenario.description}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {hideOpenFullPage ? null : (
            <Link
              to="/scenarios/$slug"
              params={{ slug: scenario.slug }}
              preload="intent"
              className="flex items-center gap-1.5 rounded-hud border border-bd-bright bg-card px-3 py-1.5 text-[11px] text-txt no-underline transition-colors hover:border-neon/40 hover:text-white"
            >
              Open full page
              <ExternalLink className="size-3.5" />
            </Link>
          )}
          <button
            type="button"
            onClick={onDeploy}
            className="flex items-center gap-1.5 rounded-hud bg-neon px-3.5 py-1.5 text-[11px] font-semibold text-void transition-opacity hover:opacity-90"
          >
            <Rocket className="size-3.5" />
            Deploy range
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile icon={<Server className="size-3.5" />} label="VMs" value={String(scenario.vmCount)} />
        <StatTile
          icon={<Layers className="size-3.5" />}
          label="Phases"
          value={String(phaseCount)}
        />
        <StatTile
          icon={<Crosshair className="size-3.5" />}
          label="Objectives"
          value={String(scenario.objectives.length)}
        />
        <StatTile
          icon={<PlayCircle className="size-3.5" />}
          label="$/hour"
          value={`$${scenario.costPerHour.toFixed(2)}`}
        />
      </div>

      {/* Topology preview */}
      <div className="mb-6">
        <TopologyPreview vms={scenario.vms} />
      </div>

      {/* Kill chain */}
      <section className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Crosshair className="size-4 text-hud-red" />
          <h2 className="font-display text-sm font-bold text-txt-bright">Kill chain</h2>
          <span className="text-[10px] text-txt-muted">
            {phaseCount} {phaseCount === 1 ? 'phase' : 'phases'} · MITRE ATT&amp;CK
          </span>
        </div>

        {phaseCount === 0 ? (
          <div className="card-hud p-4 text-[12px] text-txt-muted">
            No phases defined for this scenario.
          </div>
        ) : (
          <ol className="relative space-y-2.5">
            {scenario.phases.map((phase, i) => {
              const isEntry = i === 0;
              const isLast = i === phaseCount - 1;
              const targets = phase.targetVms ?? [];
              const ruleIds = phase.expectedDetections ?? [];
              return (
                <li key={phase.id} className="relative">
                  <div
                    className={`card-hud p-3 ${
                      isEntry ? 'border-neon/40 bg-neon-glow' : 'border-bd'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex shrink-0 flex-col items-center">
                        <span
                          className={`grid size-7 place-items-center rounded-full border font-mono text-[11px] font-bold ${
                            isEntry
                              ? 'border-neon bg-neon/15 text-neon'
                              : 'border-bd-bright bg-panel text-txt-2'
                          }`}
                        >
                          {i + 1}
                        </span>
                        {!isLast && <span className="mt-1 min-h-[12px] w-px flex-1 bg-bd" />}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display text-sm font-semibold capitalize leading-tight text-txt-bright">
                            {phase.name}
                          </span>
                          {phase.technique ? (
                            <span className="rounded border border-hud-cyan/40 bg-hud-cyan/10 px-1.5 py-0.5 font-mono text-[9px] text-hud-cyan">
                              {phase.technique}
                            </span>
                          ) : null}
                          {isEntry ? (
                            <span className="flex items-center gap-1 rounded-full border border-neon/40 bg-neon/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-neon">
                              <Target className="size-2.5" /> entry point
                            </span>
                          ) : (
                            <span className="rounded-full border border-bd bg-panel px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-txt-muted">
                              planned
                            </span>
                          )}
                        </div>

                        {phase.description ? (
                          <p className="mt-1 text-[11.5px] leading-snug text-txt-2">
                            {phase.description}
                          </p>
                        ) : null}

                        <div className="mt-2 flex flex-col gap-1.5 font-mono text-[10px]">
                          {targets.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="flex items-center gap-1 text-txt-muted">
                                <Server className="size-3" /> target
                              </span>
                              {targets.map((vm) => (
                                <span
                                  key={vm}
                                  className="rounded border border-bd bg-card px-1.5 py-0.5 text-txt-2"
                                >
                                  {vm}
                                </span>
                              ))}
                            </div>
                          ) : null}

                          {ruleIds.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="flex items-center gap-1 text-txt-muted">
                                <ShieldAlert className="size-3" /> detects
                              </span>
                              {ruleIds.map((id) => {
                                const sev = severityByRule.get(id);
                                const style =
                                  sev !== undefined
                                    ? SEVERITY_STYLE[sev]
                                    : 'bg-card border-bd text-txt-2';
                                return (
                                  <span
                                    key={id}
                                    className={`rounded border px-1.5 py-0.5 ${style}`}
                                    title={sev ? `severity: ${sev}` : undefined}
                                  >
                                    {id}
                                  </span>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Objectives */}
      <section className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Target className="size-4 text-neon" />
          <h2 className="font-display text-sm font-bold text-txt-bright">Objectives</h2>
          <span className="text-[10px] text-txt-muted">
            {scenario.objectives.length} graded
          </span>
        </div>

        {scenario.objectives.length === 0 ? (
          <div className="card-hud p-4 text-[12px] text-txt-muted">
            No objectives defined.
          </div>
        ) : (
          <div className="space-y-2.5">
            {scenario.objectives.map((obj) => (
              <div key={obj.id} className="card-hud p-3">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-display text-sm font-semibold text-txt-bright">
                    {obj.title}
                  </span>
                  <span className="shrink-0 rounded-full border border-neon/40 bg-neon-glow px-2 py-0.5 font-mono text-[9px] text-neon">
                    {obj.points} pts
                  </span>
                </div>
                <p className="mt-1 text-[11.5px] leading-snug text-txt-2">{obj.description}</p>
                {obj.detectionIds.length > 0 ? (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-[10px]">
                    <span className="flex items-center gap-1 text-txt-muted">
                      <ShieldAlert className="size-3" /> detects
                    </span>
                    {obj.detectionIds.map((id) => {
                      const sev = severityByRule.get(id);
                      const style =
                        sev !== undefined ? SEVERITY_STYLE[sev] : 'bg-card border-bd text-txt-2';
                      return (
                        <span key={id} className={`rounded border px-1.5 py-0.5 ${style}`}>
                          {id}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MITRE techniques */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <ShieldAlert className="size-4 text-hud-amber" />
          <h2 className="font-display text-sm font-bold text-txt-bright">MITRE ATT&amp;CK</h2>
          <span className="text-[10px] text-txt-muted">
            {scenario.techniques.length} techniques
          </span>
        </div>
        {scenario.techniques.length === 0 ? (
          <div className="card-hud p-4 text-[12px] text-txt-muted">
            No techniques tagged.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {scenario.techniques.map((t) => (
              <span
                key={t}
                className="rounded border border-hud-amber/40 bg-hud-amber/10 px-2 py-0.5 font-mono text-[10px] text-hud-amber"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </section>

      <div className="mt-5 flex items-center gap-1.5 text-[10px] text-txt-muted">
        <PlayCircle className="size-3" />
        Deploy this scenario to run the chain live.
      </div>
    </div>
  );
}

export default ScenarioDetailPanel;
